import axios from 'axios';

export const REGISTER_SUCCESS = 'REGISTER_SUCCESS';
export const REGISTER_FAIL = 'REGISTER_FAIL';
export const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
export const LOGIN_FAIL = 'LOGIN_FAIL';
export const LOGOUT = 'LOGOUT';
export const FETCH_USER_DATA_SUCCESS = 'FETCH_USER_DATA_SUCCESS';
export const FETCH_USER_DATA_FAIL = 'FETCH_USER_DATA_FAIL';
export const REFRESH_TOKEN_SUCCESS = 'REFRESH_TOKEN_SUCCESS';
export const REFRESH_TOKEN_FAIL = 'REFRESH_TOKEN_FAIL';
export const SET_GITHUB_USERNAME_SUCCESS = 'SET_GITHUB_USERNAME_SUCCESS';
export const SET_GITHUB_USERNAME_FAIL = 'SET_GITHUB_USERNAME_FAIL';

const API_URL = 'https://api.agent.market/v1/auth';

export const register = (email, username, fullname, password) => async dispatch => {
  try {
    const response = await axios.post(`${API_URL}/register`, {
      email,
      username,
      fullname,
      password,
    });
    dispatch({ type: REGISTER_SUCCESS, payload: response.data });
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.detail || error.message || 'Registration failed';
    dispatch({ type: REGISTER_FAIL, payload: { detail: errorMessage } });
    throw error;
  }
};

export const loginWithCredentials = (email, password) => async dispatch => {
  try {
    const response = await fetch(`${API_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        username: email,
        password: password,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'Login failed');
    }

    const data = await response.json();

    localStorage.setItem('authToken', data.access_token);
    localStorage.setItem('isAuthenticated', 'true');

    dispatch({
      type: LOGIN_SUCCESS,
      payload: data.access_token,
    });

    // Start token refresh cycle
    startTokenRefreshCycle(dispatch);

    return data;
  } catch (error) {
    const errorMessage = error.message || 'Login failed';
    dispatch({ type: LOGIN_FAIL, payload: { detail: errorMessage } });
    throw error;
  }
};

export const setGithubUsername = github_username => async (dispatch, getState) => {
  try {
    const {
      auth: { token },
    } = getState();
    if (!token) {
      throw new Error('No auth token found');
    }
    const response = await fetch(
      `${API_URL}/set-github-username?github_username=${encodeURIComponent(github_username)}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.detail || 'Failed to set GitHub username');
    }
    const data = await response.json();
    dispatch({ type: SET_GITHUB_USERNAME_SUCCESS, payload: data });
  } catch (error) {
    console.error('Error setting GitHub username:', error);
    dispatch({ type: SET_GITHUB_USERNAME_FAIL, payload: error.message });
    throw error;
  }
};

export const login = token => dispatch => {
  localStorage.setItem('authToken', token);
  localStorage.setItem('isAuthenticated', 'true');

  dispatch({
    type: LOGIN_SUCCESS,
    payload: token,
  });

  // Start token refresh cycle
  startTokenRefreshCycle(dispatch);
};

export const logout = () => dispatch => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('isAuthenticated');
  dispatch({ type: LOGOUT });
};

export const refreshToken = () => async dispatch => {
  try {
    // For JWT tokens from custom API, we might need to implement refresh logic
    // For now, we'll handle token expiration by redirecting to login
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('No auth token found');
    }

    // Custom API token refresh logic would go here
    // Since the endpoints provided don't show a refresh endpoint,
    // we'll assume tokens are long-lived or handle expiration differently

    dispatch({ type: REFRESH_TOKEN_SUCCESS, payload: token });
    return token;
  } catch (error) {
    console.error('Error refreshing token:', error);
    dispatch({ type: REFRESH_TOKEN_FAIL });
    dispatch(logout());
    throw error;
  }
};

// Helper function to manage token refresh cycle
const startTokenRefreshCycle = dispatch => {
  // For custom API tokens, adjust refresh interval as needed
  // Assuming longer-lived tokens, refresh every 12 hours
  const REFRESH_INTERVAL = 12 * 60 * 60 * 1000;

  const refreshCycle = async () => {
    try {
      await dispatch(refreshToken());
      setTimeout(refreshCycle, REFRESH_INTERVAL);
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };

  // Start the cycle
  setTimeout(refreshCycle, REFRESH_INTERVAL);
};

export const fetchUserData = () => async (dispatch, getState) => {
  try {
    const {
      auth: { token },
    } = getState();

    if (!token) {
      const error = new Error('No auth token found');
      dispatch({ type: FETCH_USER_DATA_FAIL, payload: error.message });
      throw error;
    }

    // Fetch user data from custom API using correct endpoint
    const response = await fetch(`${API_URL}/users/me/`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Failed to fetch user data' }));
      const error = new Error(errorData.detail || `HTTP ${response.status}`);
      error.response = { status: response.status };
      dispatch({ type: FETCH_USER_DATA_FAIL, payload: error.message });
      throw error;
    }

    const userData = await response.json();
    dispatch({ type: FETCH_USER_DATA_SUCCESS, payload: userData });
    return userData;
  } catch (error) {
    console.error('Error fetching user data:', error);
    dispatch({ type: FETCH_USER_DATA_FAIL, payload: error.message });
    throw error;
  }
};
